/**
 * A site's page (JSS#1157): header and actions, the degraded notice, and its
 * tabs. The tabs are the SITE_TABS array (JSS#1158): Overview, Nodes,
 * Releases, Health, and the Engineer-only Advanced tab with the raw
 * Kubernetes views. The open tab lives in the URL (`?tab=health`,
 * `?tab=advanced&sub=pods`) so a link can land on it.
 */
import { useState, type ReactNode } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, ArrowLeft, MapPin, Pencil, Trash2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { sitesApi, machinesApi } from '@/api/client';
import { RoleGate } from '@/components/auth/RoleGate';
import { useUserRoles } from '@/contexts/AuthContext';
import { Role, canActAs } from '@jeket-cloud/types/auth';
import type { Machine, Site } from '@/types';
import NodesTab from '../cluster/NodesTab';
import HealthTab from '../cluster/HealthTab';
import ReleasesPanel from '../cluster/ReleasesPanel';
import AdvancedTab, {
  DEFAULT_ADVANCED_SUB_TAB,
  isAdvancedSubTab,
  type AdvancedSubTab,
} from '../cluster/AdvancedTab';
import SiteDecommissionDialog from '../cluster/SiteDecommissionDialog';
import { DeleteSiteDialog } from './dialogs/DeleteSiteDialog';
import { EditSiteDialog } from './dialogs/EditSiteDialog';
import { SiteOverview, useJbsAccessUrls, type JbsAccessUrls } from './SiteOverview';
import { SiteDegradedNotice, SiteHealthIndicator, SiteStatusBadge } from './shared';

// ---- Tabs ----

/** What a tab renders from: the site, its machines and its JBS access URLs. */
export interface SiteTabContext {
  siteId: string;
  site: Site;
  machines: Machine[];
  access: JbsAccessUrls;
  /** Advanced's sub-tab, from `?sub=`. */
  sub: AdvancedSubTab;
  onSubChange: (sub: AdvancedSubTab) => void;
}

export type SiteTabKey = 'overview' | 'nodes' | 'releases' | 'health' | 'advanced';

export interface SiteTab {
  key: SiteTabKey;
  label: string;
  /** Lowest persona that sees the tab (the RoleGate hierarchy); none = everyone. */
  minRole?: Role;
  render: (ctx: SiteTabContext) => ReactNode;
}

// No Machine Config tab (#859): a customer sees each box's status and gets
// guided recovery; hardware templates, extensions and config overrides are
// staff-only in the staff console (#856).
//
// JSS#1158 replaced the Cluster tab, which opened every persona on raw
// Kubernetes. Its sub-tabs are now:
//   Nodes, Node pools (Engineer)        -> Nodes
//   Releases                            -> Releases
//   Health, Events                      -> Health (with the workloads that
//                                          need attention, see HealthTab)
//   Workloads, Pods, Services, Storage,
//   Resources, Secrets                  -> Advanced (Engineer)
export const SITE_TABS: readonly SiteTab[] = [
  {
    key: 'overview',
    label: 'Overview',
    render: ({ siteId, site, access }) => <SiteOverview siteId={siteId} site={site} access={access} />,
  },
  {
    key: 'nodes',
    label: 'Nodes',
    render: ({ siteId, machines }) => <NodesTab siteId={siteId} machines={machines} />,
  },
  {
    key: 'releases',
    label: 'Releases',
    // Read-only below Site Operator; install/uninstall are <RoleGate>d (#245).
    render: ({ siteId }) => <ReleasesPanel siteId={siteId} />,
  },
  {
    key: 'health',
    label: 'Health',
    render: ({ siteId }) => <HealthTab siteId={siteId} />,
  },
  {
    key: 'advanced',
    label: 'Advanced',
    minRole: Role.Engineer,
    render: ({ siteId, sub, onSubChange }) => (
      <AdvancedTab siteId={siteId} sub={sub} onSubChange={onSubChange} />
    ),
  },
];

/** The tabs a user with `roles` sees, using the same hierarchy as <RoleGate>. */
export function visibleSiteTabs(roles: readonly string[]): SiteTab[] {
  return SITE_TABS.filter((t) => !t.minRole || canActAs(roles, t.minRole));
}

// ---- Header ----

/**
 * The site page's header actions, each gated at the role the BFF enforces on
 * its route (orchestrator backend `src/index.ts`): Edit is PATCH /sites/:id
 * (SiteOperator); Decommission and Delete are POST /sites/:id/decommission and
 * DELETE /sites/:id (Admin). Delete Site was ungated, so a Viewer saw it and
 * got a 403 (JSS#970).
 */
export function SiteHeaderActions({ onEdit, onDecommission, onDelete }: {
  onEdit: () => void;
  onDecommission: () => void;
  onDelete: () => void;
}) {
  return (
    <div className="flex items-center gap-2">
      <RoleGate role={Role.SiteOperator}>
        <button
          onClick={onEdit}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-silver-bright border border-surface-elevated rounded-lg hover:bg-surface-elevated transition-colors"
          data-testid="edit-site-button"
        >
          <Pencil size={14} />
          Edit
        </button>
      </RoleGate>
      <RoleGate role={Role.Admin}>
        <button
          onClick={onDecommission}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-amber-300 border border-amber-300/40 rounded-lg hover:bg-amber-300/10 transition-colors"
          data-testid="decommission-site-button"
        >
          <AlertTriangle size={14} />
          Decommission site
        </button>
        <button
          onClick={onDelete}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-status-error border border-status-error/30 rounded-lg hover:bg-status-error/10 transition-colors"
          data-testid="delete-site-button"
        >
          <Trash2 size={14} />
          Delete Site
        </button>
      </RoleGate>
    </div>
  );
}

// ---- Site Detail ----

export default function SiteDetail({ siteId }: { siteId: string }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const userRoles = useUserRoles();
  const tabs = visibleSiteTabs(userRoles);
  const [searchParams, setSearchParams] = useSearchParams();
  // An unknown tab, or one this persona does not see, opens Overview.
  const requestedTab = searchParams.get('tab');
  const activeTab: SiteTabKey = tabs.find((t) => t.key === requestedTab)?.key ?? 'overview';
  const requestedSub = searchParams.get('sub');
  const activeSub: AdvancedSubTab = isAdvancedSubTab(requestedSub) ? requestedSub : DEFAULT_ADVANCED_SUB_TAB;
  function selectTab(key: SiteTabKey, sub?: AdvancedSubTab) {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (key === 'overview') next.delete('tab');
        else next.set('tab', key);
        if (key === 'advanced' && sub && sub !== DEFAULT_ADVANCED_SUB_TAB) next.set('sub', sub);
        else next.delete('sub');
        return next;
      },
      { replace: true },
    );
  }
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  // Admin-tier site decommission flow with pre-flight + two-step confirm (#259).
  const [showDecommissionDialog, setShowDecommissionDialog] = useState(false);
  // Edit site dialog: surfaces PATCH-supported fields (metallb pools, name,
  // description, location, proxy address) so sites created before #X — or
  // before the relevant column landed — can be filled in without DB surgery.
  const [showEditDialog, setShowEditDialog] = useState(false);

  const updateSiteMutation = useMutation({
    mutationFn: (patch: Partial<Site>) => sitesApi.update(siteId, patch),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['site', siteId] });
      queryClient.invalidateQueries({ queryKey: ['sites'] });
      setShowEditDialog(false);
    },
  });

  const { data: site, isLoading, error } = useQuery({
    queryKey: ['site', siteId],
    queryFn: () => sitesApi.get(siteId),
    refetchInterval: 5_000,
  });

  const { data: machines } = useQuery({
    queryKey: ['machines', siteId],
    queryFn: () => machinesApi.list(siteId),
    refetchInterval: 5_000,
  });

  const access = useJbsAccessUrls(siteId, site);

  const { data: allSites } = useQuery({
    queryKey: ['sites'],
    queryFn: sitesApi.list,
  });

  if (isLoading) {
    return <div className="text-silver-muted text-sm">Loading site...</div>;
  }
  if (error || !site) {
    return <div className="text-status-error text-sm">Failed to load site</div>;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link
            to="/sites"
            className="text-silver-muted hover:text-silver-bright transition-colors"
          >
            <ArrowLeft size={20} />
          </Link>
          <div>
            <h2 className="text-xl font-bold text-silver-bright">{site.name}</h2>
            <div className="flex items-center gap-2 mt-0.5">
              <MapPin size={12} className="text-silver-muted" />
              <span className="text-xs text-silver-muted">{site.location}</span>
              <span className="text-xs text-silver-muted font-mono">| {site.clusterName}</span>
            </div>
          </div>
          <SiteHealthIndicator status={site.status} />
          <SiteStatusBadge status={site.status} reason={site.statusReason} />
        </div>
        <SiteHeaderActions
          onEdit={() => setShowEditDialog(true)}
          onDecommission={() => setShowDecommissionDialog(true)}
          onDelete={() => setShowDeleteConfirm(true)}
        />
      </div>

      <SiteDegradedNotice site={site} />

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-surface-elevated">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => selectTab(tab.key)}
            data-testid={`site-tab-${tab.key}`}
            className={cn(
              'px-4 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px',
              activeTab === tab.key
                ? 'text-gold border-gold'
                : 'text-silver-muted border-transparent hover:text-silver-bright'
            )}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {tabs.find((tab) => tab.key === activeTab)?.render({
        siteId,
        site,
        machines: machines ?? [],
        access,
        sub: activeSub,
        onSubChange: (sub) => selectTab('advanced', sub),
      })}

      {/* Edit Site Dialog */}
      {showEditDialog && (
        <EditSiteDialog
          site={site}
          onClose={() => {
            // A reopened dialog does not show the last attempt's refusal.
            updateSiteMutation.reset();
            setShowEditDialog(false);
          }}
          onSave={(patch) => updateSiteMutation.mutate(patch)}
          isSaving={updateSiteMutation.isPending}
          saveError={updateSiteMutation.isError ? updateSiteMutation.error : null}
        />
      )}

      {/* Delete Site Confirmation Dialog */}
      {showDeleteConfirm && (
        <DeleteSiteDialog
          siteId={siteId}
          siteName={site.name}
          onClose={() => setShowDeleteConfirm(false)}
          onDeleted={() => {
            queryClient.invalidateQueries({ queryKey: ['sites'] });
            navigate('/sites');
          }}
        />
      )}

      {/* Decommission Site Dialog (#259) */}
      {showDecommissionDialog && (
        <SiteDecommissionDialog
          siteId={siteId}
          siteName={site.name}
          onClose={() => setShowDecommissionDialog(false)}
          onDecommissioned={() => {
            queryClient.invalidateQueries({ queryKey: ['site', siteId] });
            queryClient.invalidateQueries({ queryKey: ['sites'] });
          }}
        />
      )}
    </div>
  );
}
